import { tDynamic } from '@i18n/helpers';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Paper from '@mui/material/Paper';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import { useMotionSafe } from '@shared/components/motion';
import { UserMenu } from '@shared/components/UserMenu';
import { motion as motionTokens, tokens } from '@theme/tokens';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { AppLogo } from './AppLogo';
import {
  type AppSidebarItem,
  RAIL_HOVER,
  RAIL_TEXT,
  RAIL_TEXT_DIM,
  SIDEBAR_WIDTH,
} from './appSidebarLayout';

export type { AppSidebarItem } from './appSidebarLayout';

/** Etched hairline that fades out at both ends — engraved, not drawn. */
const etchedDivider = (
  <div
    aria-hidden
    style={{
      height: '1px',
      margin: '0 12px',
      flexShrink: 0,
      background:
        'linear-gradient(90deg, transparent, rgba(255,255,255,0.14) 20%, rgba(255,255,255,0.14) 80%, transparent)',
    }}
  />
);

interface AppSidebarProps {
  items: readonly AppSidebarItem[];
  isActive: (path: string) => boolean;
  onNavigate?: () => void;
}

/**
 * The instrument rail: a fixed slim strip of dark anodized graphite-teal in
 * BOTH color modes — circular keys only (logo, nav, profile), labels live
 * in tooltips. No expanded state: the content owns the width.
 */
export function AppSidebar({ items, isActive, onNavigate }: AppSidebarProps) {
  const { t } = useTranslation();
  const animateSafe = useMotionSafe();

  return (
    <Paper
      component="nav"
      aria-label={t('nav.mainNavigation')}
      elevation={0}
      square
      sx={(theme) => ({
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: theme.zIndex.drawer,
        display: 'flex',
        flexDirection: 'column',
        background: tokens.gradient.rail,
        color: RAIL_TEXT,
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05), inset -1px 0 0 rgba(255,255,255,0.08)',
        overflow: 'hidden',
      })}
    >
      <Toolbar sx={{ px: 1, minHeight: 64, justifyContent: 'center', color: RAIL_TEXT }}>
        <AppLogo showLabel={false} size={36} />
      </Toolbar>
      {etchedDivider}
      <List sx={{ px: 0.75, py: 2, flex: 1, overflow: 'auto' }}>
        {items.map((item) => {
          const label = tDynamic(t, item.labelKey);
          const selected = isActive(item.path);
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5, justifyContent: 'center' }}>
              <Tooltip title={label} placement="right">
                <ListItemButton
                  component={Link}
                  to={item.path}
                  selected={selected}
                  onClick={onNavigate}
                  aria-label={label}
                  sx={{
                    py: 1.25,
                    px: 1,
                    justifyContent: 'center',
                    position: 'relative',
                    color: selected ? '#ffffff' : RAIL_TEXT_DIM,
                    '&:hover': { backgroundColor: RAIL_HOVER, color: RAIL_TEXT },
                    // The gliding pill below carries the active background.
                    '&.Mui-selected, &.Mui-selected:hover': {
                      background: 'transparent',
                      boxShadow: 'none',
                      color: '#ffffff',
                    },
                  }}
                >
                  {selected && (
                    // Active state glides between nav items as a shared-layout pill.
                    <motion.span
                      layoutId="sidebar-active-pill"
                      transition={animateSafe ? motionTokens.spring : { duration: 0 }}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: 999,
                        background: tokens.gradient.navPill,
                        boxShadow:
                          'inset 0 1px 0 rgba(255,255,255,0.22), 0 2px 12px -2px rgba(126,186,186,0.5)',
                      }}
                      aria-hidden
                    />
                  )}
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      justifyContent: 'center',
                      color: 'inherit',
                      opacity: selected ? 1 : 0.85,
                      position: 'relative',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>
      {etchedDivider}
      {/* profile — the dealer's spot at the base of the instrument panel */}
      <Toolbar sx={{ px: 1, minHeight: 64, justifyContent: 'center', flexShrink: 0 }}>
        <UserMenu variant="avatarOnly" />
      </Toolbar>
    </Paper>
  );
}
